import { round } from 'lodash-es'
import { addDays, parseISO, set as dateSet } from 'date-fns'
import { newDate, isToday } from './date'
import { FMT } from './index'
import { store } from 'state/_store'
import { getDates, setPumpkinTime, updateDates } from 'state/Orders'

/* Test scenarios to cover:
 * • get in the orders list before midnight and wait for the dates to change
 * • custom dates:
 *    ∘ repeat previous scenario, with custom dates
 *    ∘ change from today to custom, and see the console dropping the timer
 * • get in the settings and wait for the dates to silently change, then see the effect in the orders list
 * • get in either place, leave the tab in background, and come back later to see the effect
 * • get in either place, close the tab, and come back after midnight to see the change happening as well
 * • same, but with more than a day of difference (most common scenario of multiple date difference, and covers the date difference for the other scenarios as well)
 * • get in the orders list with end date = tomorrow, see no changes upon midnight, but then see changes in the next day?
 */

const ms2hours = ms => round(ms/3600000, 6)

/**
 * *********************************************** The Pumpkin Scheduler ***********************************************
 *
 * This "abstract" class groups all operations related to the timer that will change the `end` date once the day is
 * over, and then re-schedules itself. Everything is encapsulated here because all related operations do not need to be
 * made public, even state accessors... except the state setter, since that _must_ be an action inside the state's slice
 * to actually work.
 *
 * Rules:
 * - It must be scheduled during app render, so it starts up even if the user lands in the settings page. If we
 * schedule only after getting to the Orders List, they might take too long at Settings and go to Orders only past
 * midnight, and they we'll be too late.
 * - It must check the current dates during the initial scheduling, so we're sure it updates the date whenever the user
 * comes back. If they open the app at the 1st and only come back at the 4th, it should still update to the 4th.
 * - Thus, it must unschedule when the date is changed to non-today, so the user can stay in their custom listing when
 * they reopen.
 * - lastly, when there's an ID in state but without a set timer, it means the user needs an update but left the app.
 * We got to check if it should have fired by now, and update accordingly.
 * - bonus: if there's more than a week we've been moving the end date forward only via the Pumpkin Timer, we should
 * also move the start date, as otherwise the user might end up with orders from many months bloating their UI
 * - With this mechanism of checking the date during schedule and not during firing, it's safe to just update when the
 * final function fires - if it shouldn't update, it won't even fire since it got unscheduled.
 *
 * > This was originally called The Midnight Scheduler, but since it may change to update after the places close, we
 * asked the fairy godmother to be a bit flexible and here we are.
 *
 * The ID is stored in `window` just like the timer from setTimeout is - if the page gets refreshed/closed, both are
 * gone, what means we need to re-schedule everything.
 *
 * While there's also a {@link Donkey} timer class, that one is based on static `setInterval()` calls, while the Pumpkin
 * depends on restaurant hours and is a beast on itself. Maybe it could be repurposed to do other stuff when the place
 * closes, but right now, better keep the ideas separate.
 *
 * @abstract
 */
export default class PumpkinTimer {

	/**
	 * Maximum difference between start and end date,
	 * @type {number}
	 */
	static MAX_STREAK = 3

	/**
	 * @param forWhen {Date} When the callback should be executed.
	 * @param callback {function} To be executed by {@link setTimeout}
	 * @return {NodeJS.Timeout|number}
	 */
	static #set(forWhen, callback) {
		const now  = newDate(null)
		const diffMs = forWhen.getTime() - now.getTime()
		console.log('Pumpkin update scheduled!')
		console.table({ when: FMT.ISO(forWhen), diffMs, inHours: ms2hours(diffMs), inMinutes: round(diffMs/60000, 3), now: FMT.ISO(now) })
		return window.PUMPKIN_UPDATE = setTimeout(callback, diffMs)
	}

	static #unset() {
		if (window.PUMPKIN_UPDATE) {
			console.log('Dropping the current pumpkin timer.')
			window.clearTimeout(window.PUMPKIN_UPDATE)
			window.PUMPKIN_UPDATE = null
		}
	}

	static #get = () => window.PUMPKIN_UPDATE

	static #getState   = () => store.getState().orders.dates.pumpkinTime
	static #getStreak  = () => store.getState().orders.dates.pumpkinStreak

	/**
	 * Checks what is currently set for the timer fields.
	 * @return {Date|boolean} True if there's nothing set, false if everything is fine; otherwise, the stored datetime (also truthy)
	 * if the timer is missing but something was previously set in the state.
	 */
	static #missing() {
		const forWhen  = PumpkinTimer.#getState()
		const windowId = PumpkinTimer.#get()
		if (!windowId && !forWhen) {
			return true
		}
		if (forWhen && !windowId) {
			const whenDate = parseISO(forWhen)
			const diffHours = ms2hours(whenDate.getTime() - newDate(null).getTime())
			if (diffHours > 24) { //won't happen IRL, but it's common during dev when we're playing with the system clock
				return true //let's reset the timer
			} else {
				return whenDate
			}
		} else {
			return false
		}
	}

	/**
	 * Simplification over {@link #missing}, just to know if a timer is correctly set or not. If used after the app
	 * initialization (when scheduling takes place), effectively means a timer is needed (and is already setup) or not.
	 * @return {boolean}
	 */
	static isset = () => !PumpkinTimer.#missing()

	/**
	 * Checks the current end date and timer status to schedule, update, or unschedule accordingly.
	 */
	static schedule = () => {
		console.group('Pumpkin Timer')
		const now   = newDate(null)
		const dates = getDates(store.getState())

		const missing = PumpkinTimer.#missing()
		let forWhen
		if (typeof(missing) == 'object') {
			console.log('It got lost between reloads. The Fairy Godmother is displeased.')
			forWhen = missing //if this is already past, it runs immediately (setTimeout gets a negative time)
		} else if (missing) {
			//nothing was set, so we must start from the ground up
			if (!isToday(parseISO(dates.end))) {
				console.log('No need to schedule; leave the user on its custom listing.')
				console.groupEnd()
				//FIXME: this actually ends with the pumpkin time loop. It should schedule a new check (call schedule again) in the next midnight?
				return
			}

			const time = [0, 0] //this would come from somewhere else, like the active restaurant's time
			forWhen    = dateSet(now, { hours: time[0], minutes: time[1], seconds: 0, milliseconds: 1 })
			// TODO missing actual restaurant hours, so we can update once they change. See ch22364
			// if (isPast(pumpkin)) { //it's 11PM and endOfDay=2:00; we need to check for the next day's 2AM instead
			forWhen = addDays(forWhen, 1)
			//}
		} else {
			console.log('Already set up and kicking. We can dismiss the godmother for now.')
			console.groupEnd()
			return
		}

		store.dispatch(setPumpkinTime(forWhen))
		PumpkinTimer.#set(forWhen, async function () {
			//this fires at least 1ms past pumpkin time (or maybe later, even days later)
			const newDay = newDate(null)
			const streak = PumpkinTimer.#getStreak()

			let startDay = null //don't advance the start date so past orders don't suddenly vanish...
			if (streak >= PumpkinTimer.MAX_STREAK) {//...unless there are days that the user didn't touch the selector
				startDay = addDays(newDay, -(PumpkinTimer.MAX_STREAK -1))
			}

			console.log('It\'s Pumpkin time!', { newDay, startDay, streak: (streak||0)+1 })
			store.dispatch(updateDates(startDay? FMT.dateISO(startDay) : null, FMT.dateISO(newDay), true))

			//we're done, so we can forget this timer's ID and reschedule again for the next pumpkin time
			await PumpkinTimer.unschedule()
			PumpkinTimer.schedule()
		})
		console.groupEnd()
	}

	static unschedule = () => {
		PumpkinTimer.#unset()
		return store.dispatch(setPumpkinTime(null))
	}
}
